import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Heart, User, Menu, X, ChevronDown, ChevronRight, Shield, LogOut, Settings, Truck, Instagram } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useCart } from '../lib/cart';
import { searchProducts, subscribeNewsletter, getTeamsBySubcategory, getWcTeams } from '../lib/api';
import { optimize } from '../lib/imageOptimizer';
import ProductImg from './ProductImg';
import { Input } from '../components/ui/input';

// Dynamic - derived from API data, no hardcoded empty categories
const LEAGUE_ORDER = [
  "Ligue 1", "La Liga", "Premier League", "Bundesliga", "Serie A",
  "Brésil Série A", "Sélections Nationales", "MLS"
];

function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [megaOpen, setMegaOpen] = useState(false);
  const [wcMenuOpen, setWcMenuOpen] = useState(false);
  const [activeLeague, setActiveLeague] = useState("Ligue 1");
  const [teamsBySubcategory, setTeamsBySubcategory] = useState({});
  const [wcTeams, setWcTeams] = useState([]);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const megaRef = useRef(null);
  const wcRef = useRef(null);
  const userMenuRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Load teams data (cached in sessionStorage; 10-min TTL).
  // IMPORTANT: only cache on success — empty/error results MUST NOT be cached.
  useEffect(() => {
    const load = async () => {
      try {
        const cached = sessionStorage.getItem('teamsBySubCache');
        if (cached) {
          try {
            const { data, ts } = JSON.parse(cached);
            // Cache must contain at least one league with teams
            if (Date.now() - ts < 10 * 60 * 1000 && data && Object.keys(data).length > 0) {
              setTeamsBySubcategory(data);
              return;
            }
          } catch { /* ignore */ }
        }
        const res = await getTeamsBySubcategory();
        const teams = res.data.teams_by_subcategory || {};
        setTeamsBySubcategory(teams);
        // Only cache when result is non-empty
        if (Object.keys(teams).length > 0) {
          sessionStorage.setItem('teamsBySubCache', JSON.stringify({ data: teams, ts: Date.now() }));
        } else {
          sessionStorage.removeItem('teamsBySubCache');
        }
      } catch (e) {
        console.error('[Layout] teamsBySubcategory failed:', e);
        sessionStorage.removeItem('teamsBySubCache');
      }
    };
    const loadWc = async () => {
      try {
        // Bumped to v2 when sort order changed to alphabetical — invalidates stale v1 caches.
        const cached = sessionStorage.getItem('wcTeamsCache_v2');
        if (cached) {
          try {
            const { data, ts } = JSON.parse(cached);
            if (Date.now() - ts < 10 * 60 * 1000 && Array.isArray(data) && data.length > 0) {
              setWcTeams(data);
              return;
            }
          } catch { /* ignore */ }
        }
        const res = await getWcTeams();
        const teams = res.data.teams || [];
        setWcTeams(teams);
        if (teams.length > 0) {
          sessionStorage.setItem('wcTeamsCache_v2', JSON.stringify({ data: teams, ts: Date.now() }));
        } else {
          sessionStorage.removeItem('wcTeamsCache_v2');
        }
      } catch (e) {
        console.error('[Layout] wcTeams failed:', e);
        sessionStorage.removeItem('wcTeamsCache_v2');
      }
    };
    load();
    loadWc();
  }, []);

  // Compute active leagues (only those with products) in the preferred order
  const activeLeagues = LEAGUE_ORDER.filter(l => teamsBySubcategory[l]?.length > 0);
  // Add any leagues from API that aren't in our order list
  Object.keys(teamsBySubcategory).forEach(l => {
    if (!activeLeagues.includes(l) && teamsBySubcategory[l]?.length > 0) activeLeagues.push(l);
  });

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(async () => {
        try {
          const res = await searchProducts(searchQuery);
          setSearchResults(res.data.products || []);
        } catch { setSearchResults([]); }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <>
      {/* Promo Banner */}
      <div className="bg-[#FF3B30] text-white text-center py-1.5 text-xs font-medium tracking-widest uppercase font-body">
        Livraison offerte sur tous nos produits &mdash; 2 maillots achetés = le 3ème offert
      </div>

      <header className="sticky top-0 z-50 glass-nav dark-region border-b border-white/5" data-testid="main-header">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Mobile menu button */}
            <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Logo */}
            <Link to="/" className="font-heading text-lg sm:text-xl font-black tracking-tighter uppercase" data-testid="logo-link">
              ONE MAILLOT
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-0 font-body text-sm" data-testid="desktop-nav">
              {/* Football with mega menu */}
              <div
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => setMegaOpen(false)}
                ref={megaRef}
              >
                <Link
                  to="/catalogue?category=Football"
                  className="px-3 py-5 text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1 uppercase tracking-wider text-xs font-medium"
                  data-testid="nav-football"
                >
                  Football <ChevronDown size={12} />
                </Link>
                {megaOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 dark-region border border-white/10 z-50 w-[640px]" data-testid="mega-menu">
                    <div className="flex">
                      {/* Left: Leagues */}
                      <div className="w-[200px] border-r border-white/5 py-3">
                        <p className="px-4 pb-2 text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-600">Ligues</p>
                        {activeLeagues.map(league => (
                          <button
                            key={league}
                            onMouseEnter={() => setActiveLeague(league)}
                            onClick={() => { navigate(`/catalogue?category=Football&subcategory=${encodeURIComponent(league)}`); setMegaOpen(false); }}
                            className={`w-full text-left px-4 py-1.5 text-sm transition-colors flex items-center justify-between ${
                              activeLeague === league ? 'text-white bg-white/5' : 'text-zinc-500 hover:text-zinc-300'
                            }`}
                            data-testid={`mega-league-${league}`}
                          >
                            {league}
                            <ChevronRight size={10} className={activeLeague === league ? 'opacity-100' : 'opacity-0'} />
                          </button>
                        ))}
                        <Link
                          to="/catalogue?category=Football"
                          className="block px-4 py-2 text-sm text-white font-medium border-t border-white/5 mt-2 pt-2 hover:bg-white/5 transition-colors"
                          onClick={() => setMegaOpen(false)}
                        >
                          Tout le football
                        </Link>
                      </div>
                      {/* Right: Teams for selected league */}
                      <div className="flex-1 py-3 px-4 min-h-[300px]">
                        <p className="pb-2 text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-600">
                          {activeLeague === "Sélections Nationales" ? "Équipes" : "Clubs"} &mdash; {activeLeague}
                        </p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                          {(teamsBySubcategory[activeLeague] || []).map(({ team, count }) => (
                            <Link
                              key={team}
                              to={`/catalogue?category=Football&subcategory=${encodeURIComponent(activeLeague)}&team=${encodeURIComponent(team)}`}
                              className="text-sm text-zinc-400 hover:text-white py-1 transition-colors truncate flex items-center justify-between"
                              onClick={() => setMegaOpen(false)}
                              data-testid={`mega-team-${team}`}
                            >
                              <span className="truncate">{team}</span>
                              <span className="text-[10px] text-zinc-600 ml-1 shrink-0">{count}</span>
                            </Link>
                          ))}
                          {(!teamsBySubcategory[activeLeague] || teamsBySubcategory[activeLeague].length === 0) && (
                            <p className="text-xs text-zinc-600 col-span-2 py-4">Bientôt disponible</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Coupe du Monde 2026 */}
              <div
                className="relative"
                onMouseEnter={() => setWcMenuOpen(true)}
                onMouseLeave={() => setWcMenuOpen(false)}
                ref={wcRef}
              >
                <Link
                  to="/catalogue?subcategory=S%C3%A9lections+Nationales"
                  className="px-3 py-5 text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1 whitespace-nowrap uppercase tracking-wider text-xs font-medium"
                  data-testid="nav-worldcup"
                >
                  Coupe du Monde 2026 <ChevronDown size={12} />
                </Link>
                {wcMenuOpen && wcTeams.length > 0 && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 dark-region border border-white/10 z-50 w-[480px]" data-testid="wc-menu">
                    <div className="p-4">
                      <div className="flex items-center justify-between pb-3 border-b border-white/5 mb-3">
                        <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-600">Sélections nationales</p>
                        <span className="text-[10px] text-zinc-600">{wcTeams.reduce((s, t) => s + t.count, 0)} maillots</span>
                      </div>
                      <div className="grid grid-cols-3 gap-x-4 gap-y-0.5">
                        {wcTeams.map(({ team, count }) => (
                          <Link
                            key={team}
                            to={`/catalogue?team=${encodeURIComponent(team)}`}
                            className="text-sm text-zinc-400 hover:text-white py-1 transition-colors truncate flex items-center justify-between"
                            onClick={() => setWcMenuOpen(false)}
                            data-testid={`wc-team-${team}`}
                          >
                            <span className="truncate">{team}</span>
                            <span className="text-[10px] text-zinc-600 ml-1 shrink-0">{count}</span>
                          </Link>
                        ))}
                      </div>
                      <Link
                        to="/catalogue?subcategory=Coupe+du+Monde+2026"
                        className="block text-sm text-white font-medium border-t border-white/5 mt-3 pt-3 hover:bg-white/5 px-1 py-1 transition-colors"
                        onClick={() => setWcMenuOpen(false)}
                      >
                        Voir toutes les sélections
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              {/* Débardeurs maillots */}
              <Link
                to="/catalogue?subcategory=D%C3%A9bardeurs+maillots"
                className="px-3 py-5 text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1.5 whitespace-nowrap uppercase tracking-wider text-xs font-medium"
                data-testid="nav-debardeurs"
              >
                Débardeurs maillots
              </Link>
              {/* Maillot Concept */}
              <Link
                to="/catalogue?subcategory=Maillot+Concept"
                className="px-3 py-5 text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1.5 whitespace-nowrap uppercase tracking-wider text-xs font-medium"
                data-testid="nav-maillot-concept"
              >
                Maillot Concept
              </Link>
              <Link
                to="/track-order"
                className="px-3 py-5 text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1.5 whitespace-nowrap uppercase tracking-wider text-xs font-medium"
                data-testid="nav-track-order"
              >
                <Truck size={14} /> Suivre mon colis
              </Link>
              {/* NBA */}
              {/* NHL */}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              <button onClick={() => setSearchOpen(!searchOpen)} className="p-2 text-zinc-400 hover:text-white transition-colors" data-testid="search-toggle">
                <Search size={18} />
              </button>
              {user && (
                <Link to="/compte" className="p-2 text-zinc-400 hover:text-white transition-colors hidden sm:block" data-testid="wishlist-link">
                  <Heart size={18} />
                </Link>
              )}
              {/* User menu dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => user ? setUserMenuOpen(!userMenuOpen) : navigate('/connexion')}
                  className={`p-2 transition-colors ${user ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
                  data-testid="account-link"
                >
                  <User size={18} />
                </button>
                {userMenuOpen && user && (
                  <div className="absolute right-0 top-full mt-1 w-52 dark-region border border-white/10 z-50 py-1" data-testid="user-dropdown">
                    <div className="px-4 py-2.5 border-b border-white/5">
                      <p className="text-xs text-white font-medium truncate">{user.name || user.email}</p>
                      <p className="text-[10px] text-zinc-500 truncate">{user.email}</p>
                    </div>
                    <Link to="/compte" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-colors" data-testid="dropdown-account">
                      <Settings size={14} /> Mon compte
                    </Link>
                    {user.is_admin && (
                      <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-yellow-500 hover:text-yellow-400 hover:bg-white/5 transition-colors" data-testid="dropdown-admin">
                        <Shield size={14} /> Panel Admin
                      </Link>
                    )}
                    <div className="border-t border-white/5 mt-1 pt-1">
                      <button onClick={() => { logout(); setUserMenuOpen(false); navigate('/'); }} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#FF3B30] hover:text-red-400 hover:bg-white/5 transition-colors w-full text-left" data-testid="dropdown-logout">
                        <LogOut size={14} /> Déconnexion
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <Link to="/panier" className="p-2 text-zinc-400 hover:text-white transition-colors relative" data-testid="cart-link">
                <ShoppingBag size={18} />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#FF3B30] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center">
                    {count}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* Search overlay */}
        {searchOpen && (
          <div ref={searchRef} className="absolute top-full left-0 right-0 dark-region border-b border-white/10 p-4 z-50" data-testid="search-panel">
            <div className="max-w-[1400px] mx-auto">
              <Input
                type="text"
                placeholder="Rechercher un maillot, une équipe, un pays..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim().length > 0) {
                    e.preventDefault();
                    navigate(`/catalogue?search=${encodeURIComponent(searchQuery.trim())}`);
                    setSearchOpen(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }
                }}
                className="bg-transparent border-white/10 text-white placeholder:text-zinc-500 h-12 text-base"
                autoFocus
                data-testid="search-input"
              />
              {searchResults.length > 0 && (
                <div className="mt-3 space-y-1 max-h-80 overflow-y-auto">
                  {searchResults.map(p => (
                    <Link
                      key={p.product_id}
                      to={`/produit/${p.slug}`}
                      className="flex items-center gap-3 p-2 hover:bg-white/5 transition-colors"
                      onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                      data-testid={`search-result-${p.slug}`}
                    >
                      <div className="w-10 h-10 shrink-0 bg-zinc-800 overflow-hidden">
                        <ProductImg src={p.images[0]} alt={p.name} width={80} />
                      </div>
                      <div>
                        <div className="text-sm text-white">{p.name}</div>
                        <div className="text-xs text-zinc-500">{p.price.toFixed(2)} €</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden dark-region border-b border-white/10 max-h-[70vh] overflow-y-auto" data-testid="mobile-nav">
            <nav className="py-4 px-4 font-body">
              {/* Football accordion */}
              <button
                onClick={() => setMobileExpanded(mobileExpanded === 'football' ? null : 'football')}
                className="w-full flex items-center justify-between py-3 text-zinc-300 border-b border-white/5 text-sm uppercase tracking-wider"
              >
                Football <ChevronDown size={14} className={`transition-transform ${mobileExpanded === 'football' ? 'rotate-180' : ''}`} />
              </button>
              {mobileExpanded === 'football' && (
                <div className="py-2 pl-3 space-y-0.5 border-b border-white/5">
                  {activeLeagues.map(league => (
                    <Link
                      key={league}
                      to={`/catalogue?category=Football&subcategory=${encodeURIComponent(league)}`}
                      className="block py-1.5 text-zinc-500 hover:text-white text-sm"
                      onClick={() => setMobileOpen(false)}
                    >
                      {league}
                    </Link>
                  ))}
                  <Link to="/catalogue?category=Football" className="block py-1.5 text-white text-sm font-medium" onClick={() => setMobileOpen(false)}>
                    Tout le football
                  </Link>
                </div>
              )}
              {/* NBA/NHL removed - football only */}
              {/* Coupe du Monde 2026 accordion */}
              <button
                onClick={() => setMobileExpanded(mobileExpanded === 'worldcup' ? null : 'worldcup')}
                className="w-full flex items-center justify-between py-3 text-zinc-300 border-b border-white/5 text-sm uppercase tracking-wider"
              >
                Coupe du Monde 2026 <ChevronDown size={14} className={`transition-transform ${mobileExpanded === 'worldcup' ? 'rotate-180' : ''}`} />
              </button>
              {mobileExpanded === 'worldcup' && (
                <div className="py-2 pl-3 space-y-0.5 border-b border-white/5">
                  {wcTeams.map(({ team }) => (
                    <Link
                      key={team}
                      to={`/catalogue?team=${encodeURIComponent(team)}`}
                      className="block py-1.5 text-zinc-500 hover:text-white text-sm"
                      onClick={() => setMobileOpen(false)}
                    >
                      {team}
                    </Link>
                  ))}
                  <Link to="/catalogue?subcategory=Coupe+du+Monde+2026" className="block py-1.5 text-white text-sm font-medium" onClick={() => setMobileOpen(false)}>
                    Toutes les sélections
                  </Link>
                </div>
              )}
              <Link
                to="/catalogue?subcategory=D%C3%A9bardeurs+maillots"
                className="flex items-center justify-between py-3 text-zinc-300 border-b border-white/5 text-sm uppercase tracking-wider"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-debardeurs"
              >
                Débardeurs maillots
                <ChevronRight size={14} />
              </Link>
              <Link
                to="/catalogue?subcategory=Maillot+Concept"
                className="flex items-center justify-between py-3 text-zinc-300 border-b border-white/5 text-sm uppercase tracking-wider"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-maillot-concept"
              >
                Maillot Concept
                <ChevronRight size={14} />
              </Link>
              {/* Suivre mon colis (mobile) */}
              <Link
                to="/track-order"
                className="flex items-center justify-between py-3 text-zinc-300 border-b border-white/5 text-sm uppercase tracking-wider"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-track-order"
              >
                <span className="inline-flex items-center gap-2"><Truck size={14} /> Suivre mon colis</span>
                <ChevronRight size={14} />
              </Link>
              <div className="pt-4 space-y-2">
                {user ? (
                  <>
                    <Link to="/compte" className="block py-2 text-zinc-400 text-sm" onClick={() => setMobileOpen(false)}>Mon compte</Link>
                    <button onClick={() => { logout(); setMobileOpen(false); }} className="block py-2 text-zinc-400 text-sm">Déconnexion</button>
                  </>
                ) : (
                  <Link to="/connexion" className="block py-2 text-zinc-400 text-sm" onClick={() => setMobileOpen(false)}>Connexion</Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}

function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await subscribeNewsletter(email);
      setSubscribed(true);
      setEmail('');
    } catch { /* ignore */ }
  };

  return (
    <footer className="dark-region border-t border-white/5 mt-24" data-testid="footer">
      {/* Newsletter */}
      <div className="border-b border-white/5 py-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">Newsletter</p>
          <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-4">
            Reste dans le game
          </h3>
          <p className="text-zinc-400 font-body mb-6 max-w-md mx-auto text-sm">
            Nouveautés, offres exclusives et drops en avant-première. Directement dans ta boîte.
          </p>
          {subscribed ? (
            <p className="text-[#22c55e] font-body text-sm" data-testid="newsletter-success">Inscription réussie !</p>
          ) : (
            <form onSubmit={handleNewsletter} className="flex max-w-md mx-auto" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="ton@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent border-white/10 text-white placeholder:text-zinc-600 h-11"
                required
                data-testid="newsletter-input"
              />
              <button type="submit" className="bg-white text-black font-bold text-sm px-6 h-11 hover:bg-zinc-200 transition-colors uppercase tracking-wider" data-testid="newsletter-submit">
                OK
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Footer links */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 font-body text-sm">
        <div>
          <h4 className="font-heading text-xs font-bold tracking-[0.15em] uppercase mb-4 text-white">Boutique</h4>
          <div className="space-y-2">
            <Link to="/catalogue" className="block text-zinc-500 hover:text-white transition-colors">Catalogue</Link>
            <Link to="/catalogue?badge=nouveau" className="block text-zinc-500 hover:text-white transition-colors">Nouveautés</Link>
            <Link to="/catalogue?badge=best-seller" className="block text-zinc-500 hover:text-white transition-colors">Best-sellers</Link>
            <Link to="/catalogue?badge=promo" className="block text-zinc-500 hover:text-white transition-colors">Promotions</Link>
          </div>
        </div>
        <div>
          <h4 className="font-heading text-xs font-bold tracking-[0.15em] uppercase mb-4 text-white">Information</h4>
          <div className="space-y-2">
            <Link to="/a-propos" className="block text-zinc-500 hover:text-white transition-colors">À propos</Link>
            <Link to="/track-order" className="block text-zinc-500 hover:text-white transition-colors" data-testid="footer-track-link">Suivi de commande</Link>
            <Link to="/livraison" className="block text-zinc-500 hover:text-white transition-colors">Politique d'expédition</Link>
            <Link to="/retours" className="block text-zinc-500 hover:text-white transition-colors">Politique de remboursement</Link>
            <Link to="/faq" className="block text-zinc-500 hover:text-white transition-colors">FAQ</Link>
            <Link to="/contact" className="block text-zinc-500 hover:text-white transition-colors">Coordonnées</Link>
          </div>
        </div>
        <div>
          <h4 className="font-heading text-xs font-bold tracking-[0.15em] uppercase mb-4 text-white">Légal</h4>
          <div className="space-y-2">
            <Link to="/confidentialite" className="block text-zinc-500 hover:text-white transition-colors">Politique de confidentialité</Link>
            <Link to="/mentions-legales" className="block text-zinc-500 hover:text-white transition-colors">Mentions légales</Link>
            <Link to="/cgv" className="block text-zinc-500 hover:text-white transition-colors">Conditions générales de vente</Link>
            <Link to="/conditions-utilisation" className="block text-zinc-500 hover:text-white transition-colors">Conditions d'utilisation</Link>
          </div>
        </div>
        <div>
          <h4 className="font-heading text-xs font-bold tracking-[0.15em] uppercase mb-4 text-white">One Maillot</h4>
          <p className="text-zinc-500 text-xs leading-relaxed mb-4">
            Réplique de qualité premium avec finitions haut de gamme. Les maillots les plus recherchés au même endroit.
          </p>
          <div className="flex items-center gap-3" data-testid="footer-socials">
            <a
              href="https://www.instagram.com/onemaillot"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-zinc-500 hover:text-white transition-colors"
              data-testid="social-instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              href="https://www.tiktok.com/@onemaillot"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="text-zinc-500 hover:text-white transition-colors"
              data-testid="social-tiktok"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.9a4.85 4.85 0 0 1-1.84-.21Z" />
              </svg>
            </a>
            <a
              href="https://snapchat.com/t/EztmU7Q1"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Snapchat"
              className="text-zinc-500 hover:text-white transition-colors"
              data-testid="social-snapchat"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M12.166 2c2.847.063 5.027 1.62 6.054 4.295.342.89.36 1.823.31 2.762-.028.512-.07 1.024-.106 1.535-.005.069.018.115.077.155.215.146.466.21.717.245.336.046.668.107.971.272.604.328.696 1.013.184 1.483-.245.225-.541.357-.85.456-.512.165-1.04.276-1.539.487-.595.252-.766.554-.586 1.176.336 1.16 1.034 2.073 2 2.793.514.383 1.083.66 1.71.798.345.077.515.227.523.443.011.305-.169.484-.402.62-.586.343-1.245.495-1.913.595-.213.032-.27.123-.31.328-.075.392-.198.776-.34 1.15-.13.34-.357.469-.706.43-.418-.046-.829-.144-1.246-.198-.553-.072-1.108-.07-1.656.057-.494.114-.928.336-1.317.66-.46.382-.95.713-1.522.918-1.247.448-2.487.43-3.708-.092-.55-.235-1.018-.587-1.461-.984-.45-.403-.967-.65-1.561-.74-.66-.1-1.319-.092-1.976.02-.262.045-.526.094-.79.119-.314.03-.518-.104-.64-.39-.158-.371-.297-.75-.382-1.146-.062-.286-.218-.366-.473-.4-.581-.077-1.157-.183-1.701-.41-.301-.126-.585-.275-.776-.561-.211-.317-.13-.65.221-.811.279-.127.581-.187.875-.272 1.176-.341 2.052-1.05 2.677-2.097.225-.377.405-.776.512-1.205.062-.246-.005-.39-.241-.498-.413-.187-.847-.32-1.273-.477-.293-.108-.585-.227-.847-.402-.51-.34-.485-.911.022-1.265.293-.205.629-.298.973-.36.305-.054.609-.106.866-.295.078-.057.106-.121.101-.215-.045-.726-.114-1.452-.122-2.18-.013-1.207.227-2.35.847-3.397 1.005-1.7 2.479-2.793 4.382-3.281.512-.131 1.04-.195 1.581-.222Z" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <p className="text-zinc-600 text-xs font-body">&copy; 2026, One Maillot &mdash; Boutique</p>
          <div className="flex flex-wrap gap-4 text-xs font-body">
            <Link to="/confidentialite" className="text-zinc-600 hover:text-white transition-colors">Politique de confidentialité</Link>
            <Link to="/mentions-legales" className="text-zinc-600 hover:text-white transition-colors">Mentions légales</Link>
            <Link to="/contact" className="text-zinc-600 hover:text-white transition-colors">Coordonnées</Link>
            <Link to="/cgv" className="text-zinc-600 hover:text-white transition-colors">CGV</Link>
            <Link to="/livraison" className="text-zinc-600 hover:text-white transition-colors">Expédition</Link>
            <Link to="/conditions-utilisation" className="text-zinc-600 hover:text-white transition-colors">Conditions d'utilisation</Link>
            <Link to="/retours" className="text-zinc-600 hover:text-white transition-colors">Remboursement</Link>
          </div>
        </div>
      </div>

    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
